import { Loader } from '@googlemaps/js-api-loader';
import { APP } from '../config/app';
import { choosePreferredDrivingRoute } from '../utils/routeSelection';

let mapsPromise = null;

export const SERVICE_RADIUS_M = APP.serviceRadiusKm * 1000;

export const loadGoogleMaps = () => {
  if (mapsPromise) return mapsPromise;
  const key = process.env.REACT_APP_GOOGLE_MAPS_KEY || process.env.REACT_APP_FIREBASE_API_KEY;
  if (!key) return Promise.reject(new Error('Missing Google Maps API key'));

  const loader = new Loader({
    apiKey: key,
    version: 'quarterly',
    region: 'IN',
    language: 'en',
    libraries: ['geometry', 'marker'],
    authReferrerPolicy: 'origin',
  });

  mapsPromise = loader.load();
  return mapsPromise;
};

function circleBounds(center, radiusKm) {
  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / (111 * Math.cos((center.lat * Math.PI) / 180));
  return {
    north: center.lat + latDelta,
    south: center.lat - latDelta,
    east: center.lng + lngDelta,
    west: center.lng - lngDelta,
  };
}

export const createMap = async (container, center = APP.mapCenter) => {
  const google = await loadGoogleMaps();
  const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || process.env.REACT_APP_GOOGLE_MAPS_DRIVER_MAP_ID || undefined;
  const mapOptions = {
    center,
    zoom: 14,
    disableDefaultUI: true,
    zoomControl: true,
    gestureHandling: 'greedy',
    clickableIcons: false,
    isFractionalZoomEnabled: false,
  };

  if (mapId) {
    mapOptions.mapId = mapId;
  } else {
    mapOptions.styles = [
      { featureType: 'poi', stylers: [{ visibility: 'simplified' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { elementType: 'geometry', stylers: [{ color: '#f0f4f0' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
      { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#4a5a52' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c8e6d9' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#3d4f45' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#f0f4f0' }] },
    ];
  }

  const map = new google.maps.Map(container, mapOptions);

  new google.maps.Circle({
    map,
    center: APP.mapCenter,
    radius: SERVICE_RADIUS_M,
    fillColor: '#00A86B',
    fillOpacity: 0.07,
    strokeColor: '#00A86B',
    strokeOpacity: 0.35,
    strokeWeight: 1.5,
    clickable: false,
  });

  return map;
};

export const getDirections = async (origin, destination) => {
  const google = await loadGoogleMaps();
  const service = new google.maps.DirectionsService();

  return new Promise((resolve, reject) => {
    service.route(
      {
        origin: { lat: origin.lat, lng: origin.lng },
        destination: { lat: destination.lat, lng: destination.lng },
        travelMode: google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: true,
      },
      (result, status) => {
        if (status !== 'OK' || !result?.routes?.[0]) {
          reject(new Error('Could not calculate route'));
          return;
        }
        const route = choosePreferredDrivingRoute(result.routes);
        const leg = route?.legs?.[0];
        if (!route || !leg) {
          reject(new Error('Could not calculate route'));
          return;
        }
        resolve({
          distanceText: leg.distance?.text || '',
          durationText: leg.duration?.text || '',
          polyline: route.overview_path,
        });
      },
    );
  });
};

export const drawRoute = async (map, path) => {
  const google = await loadGoogleMaps();
  return new google.maps.Polyline({
    path,
    geodesic: true,
    strokeColor: '#00A86B',
    strokeOpacity: 0.9,
    strokeWeight: 5,
    map,
  });
};

export const placeMarker = async (map, position, label, color = '#00d68f') => {
  const google = await loadGoogleMaps();
  return new google.maps.Marker({
    map,
    position,
    label: label ? { text: label, color: '#fff', fontWeight: '700' } : undefined,
    icon: label ? undefined : {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 10,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#fff',
      strokeWeight: 2,
    },
  });
};

/** Live self-location for Anna — large blue pin so it’s obvious on the map. */
export const placeSelfMarker = async (map, position) => {
  const google = await loadGoogleMaps();
  return new google.maps.Marker({
    map,
    position,
    zIndex: 9999,
    title: 'You',
    optimized: false,
    label: {
      text: '●',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '700',
    },
    icon: {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 14,
      fillColor: '#1A73E8',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 4,
    },
  });
};

// Glide duration is clamped to this ceiling even when the real gap since the
// last fix was longer. DriverContext throttles the feed this reads from to
// at most one update per ~10s while stationary, faster while actually
// driving (see DriverContext.js's myPosStateAtRef check) — 2.5s keeps the
// glide resolved well before the next fix lands during a normal drive,
// instead of perpetually chasing a moving target.
const SELF_GLIDE_MAX_MS = 2500;
// Past this gap (backgrounded app, parked a while, GPS stall) the position
// jump is real ground covered without a fix, not something to animate as
// continuous driving — snap straight to the true fix instead.
const SELF_GLIDE_SNAP_GAP_MS = 15000;

export const updateSelfMarker = (marker, position) => {
  if (!marker || !position?.lat || !position?.lng) return;
  const current = marker.getPosition?.();
  const fromLat = current ? current.lat() : position.lat;
  const fromLng = current ? current.lng() : position.lng;
  if (fromLat === position.lat && fromLng === position.lng) return;

  if (marker.__mtAnimFrame) {
    cancelAnimationFrame(marker.__mtAnimFrame);
    marker.__mtAnimFrame = null;
  }

  const now = performance.now();
  const gap = marker.__mtLastAt ? now - marker.__mtLastAt : 0;
  marker.__mtLastAt = now;

  if (!gap || gap > SELF_GLIDE_SNAP_GAP_MS) {
    marker.setPosition(position);
    return;
  }

  const glideMs = Math.min(Math.max(gap, 300), SELF_GLIDE_MAX_MS);
  const startTime = now;
  const from = { lat: fromLat, lng: fromLng };

  const animate = (t) => {
    const progress = Math.min((t - startTime) / glideMs, 1); // linear, same as the passenger auto marker
    marker.setPosition({
      lat: from.lat + (position.lat - from.lat) * progress,
      lng: from.lng + (position.lng - from.lng) * progress,
    });
    if (progress < 1) marker.__mtAnimFrame = requestAnimationFrame(animate);
  };
  marker.__mtAnimFrame = requestAnimationFrame(animate);
};

export const fitMapToPoints = (map, points, padding = 64, maxZoom = 15.5) => {
  if (!map || !points?.length) return;
  const bounds = new window.google.maps.LatLngBounds();
  points.forEach((p) => {
    if (p?.lat != null && p?.lng != null) bounds.extend(p);
  });
  if (bounds.isEmpty()) return;
  const pad = typeof padding === 'number'
    ? { top: padding, right: padding, bottom: padding, left: padding }
    : padding;
  map.fitBounds(bounds, pad);

  // Cap zoom level so single or close points don't zoom into an isolated blank patch
  if (window.google?.maps?.event) {
    const listener = window.google.maps.event.addListener(map, 'idle', () => {
      if (map.getZoom() > maxZoom) {
        map.setZoom(maxZoom);
      }
      window.google.maps.event.removeListener(listener);
    });
  }
};

export const clearMarkers = (markers) => {
  markers.forEach((m) => m?.setMap(null));
  markers.length = 0;
};

export const distanceBetweenM = (a, b) => {
  if (!a || !b || a.lat == null || b.lat == null) return Infinity;
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

export const getCurrentPosition = (opts = {}) =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        heading: pos.coords.heading,
        accuracy: pos.coords.accuracy,
      }),
      (err) => reject(err),
      {
        enableHighAccuracy: true,
        timeout: opts.timeout ?? 15000,
        maximumAge: opts.maximumAge ?? 5000,
      },
    );
  });

/** UI watch — updates more often than the RTDB publisher throttle. */
export const watchPosition = (onUpdate, onError, opts = {}) => {
  if (!navigator.geolocation) return null;
  let lastAt = 0;
  let lastPos = null;
  const minMs = opts.minMs ?? 1500;
  const minMeters = opts.minMeters ?? 8;
  return navigator.geolocation.watchPosition(
    (pos) => {
      const point = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        heading: pos.coords.heading,
        accuracy: pos.coords.accuracy,
      };
      const now = Date.now();
      if (lastPos && now - lastAt < minMs) {
        const dlat = (point.lat - lastPos.lat) * 111000;
        const dlng = (point.lng - lastPos.lng) * 111000 * Math.cos((point.lat * Math.PI) / 180);
        if (Math.hypot(dlat, dlng) < minMeters) return;
      }
      lastAt = now;
      lastPos = point;
      onUpdate(point);
    },
    onError,
    { enableHighAccuracy: true, maximumAge: 2000, timeout: 20000 },
  );
};

export const isBillingError = (message = '') =>
  message.includes('BillingNotEnabled') || message.includes('billing');
